import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SaveIcon from "@mui/icons-material/Save";
import axiosInstance from "API/axiosInstance";
import { decryptText } from "config/encryption";
import { API_ENDPOINTS } from "config/api";
import MDButton from "components/MDButton";

// Get staffId from localStorage (token is handled by axiosInstance)
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return {
          staffId: parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null
        };
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return { staffId: null };
};

const AdminChapterManager = ({ onClose = () => {} }) => {
  const { staffId } = getAdminSession();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editChapter, setEditChapter] = useState(null);
  const [form, setForm] = useState({
    chapterId: 0,
    chapterName: "",
    chapterShortName: "",
    location: "",
    description: "",
    isActive: true,
    createdUserId: 1,
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    chapterId: null,
  });
  const [page, setPage] = useState(1);
  const cardsPerPage = 2;

  const handlePageChange = (event, value) => setPage(value);

  const fetchChapters = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTERS);
      setChapters(res.data);
    } catch (err) {
      console.error("Error fetching chapters:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch chapters",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChapters();
  }, []);

  useEffect(() => {
    const totalPages = Math.ceil(chapters.length / cardsPerPage);
    if (page > totalPages) {
      setPage(totalPages || 1);
    } else if (page < 1) {
      setPage(1);
    }
  }, [chapters, page]);

  const handleSnackbarClose = () =>
    setSnackbar({ ...snackbar, open: false });

  const handleDialogOpen = (chapter = null) => {
    setEditChapter(chapter);
    setForm(
      chapter
        ? { ...chapter }
        : {
            chapterId: 0,
            chapterName: "",
            chapterShortName: "",
            location: "",
            description: "",
            isActive: true,
            createdUserId: 1,
          }
    );
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditChapter(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, createdUserId: staffId };
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.MERGE_CHAPTER, payload);
      setSnackbar({
        open: true,
        message: res.data.message || "Saved successfully",
        severity: "success",
      });
      setDialogOpen(false);
      fetchChapters();
    } catch (err) {
      console.error("Error saving chapter:", err);
      setSnackbar({
        open: true,
        message: "Failed to save chapter",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.DELETE_CHAPTER, {
        chapterId: deleteDialog.chapterId,
        deletedBy: staffId,
      });
      setSnackbar({
        open: true,
        message: res.data.message || "Deleted successfully",
        severity: "success",
      });
      setDeleteDialog({ open: false, chapterId: null });
      fetchChapters();
    } catch (err) {
      console.error("Error deleting chapter:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete chapter",
        severity: "error",
      });
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: "auto",
        px: { xs: 2, sm: 3, md: 4 },
        py: 4,
        position: "relative",
        bgcolor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      {onClose && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
          <Tooltip title="Close">
            <IconButton onClick={onClose} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Manage Chapters
      </Typography>
      <MDButton
        color="info"
        variant="gradient"
        startIcon={<AddIcon />}
        onClick={() => handleDialogOpen()}
        sx={{ mb: 2 }}
      >
        Add Chapter
      </MDButton>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Grid container spacing={3} justifyContent="center">
            {chapters
              .slice((page - 1) * cardsPerPage, page * cardsPerPage)
              .map((chapter) => (
                <Grid
                  item
                  xs={12}
                  key={chapter.chapterId}
                >
                  <Card
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      boxShadow: 3,
                      bgcolor: "#fff",
                      minHeight: 220,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <GroupIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" fontWeight="bold">
                          {chapter.chapterName}
                        </Typography>
                      </Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        <LocationOnIcon
                          fontSize="small"
                          sx={{ mr: 0.5, verticalAlign: "middle" }}
                        />
                        {chapter.location}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        {chapter.description}
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                        <Chip
                          label={`Short: ${chapter.chapterShortName}`}
                          size="small"
                        />
                        {chapter.staffCode && (
                          <Chip
                            label={`Staff: ${chapter.staffCode}`}
                            size="small"
                            color="info"
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        <EventNoteIcon
                          fontSize="inherit"
                          sx={{ mr: 0.5, verticalAlign: "middle" }}
                        />
                        {chapter.createdAt
                          ? new Date(chapter.createdAt).toLocaleString()
                          : ""}
                      </Typography>
                      <Box mt={1} display="flex" justifyContent="flex-end">
                        {chapter.isActive ? (
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="Inactive"
                            color="default"
                            size="small"
                          />
                        )}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: "flex-end" }}>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleDialogOpen(chapter)}
                          color="info"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() =>
                            setDeleteDialog({
                              open: true,
                              chapterId: chapter.chapterId,
                            })
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
          </Grid>
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(chapters.length / cardsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="info"
              shape="rounded"
            />
          </Box>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>{editChapter ? "Edit Chapter" : "Add Chapter"}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Chapter Name"
            name="chapterName"
            value={form.chapterName}
            onChange={handleFormChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Short Name"
            name="chapterShortName"
            value={form.chapterShortName}
            onChange={handleFormChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Location"
            name="location"
            value={form.location}
            onChange={handleFormChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Description"
            name="description"
            value={form.description}
            onChange={handleFormChange}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleDialogClose} color="info" variant="outlined">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSave}
            color="info"
            variant="gradient"
            startIcon={<SaveIcon />}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, chapterId: null })}
      >
        <DialogTitle>Delete Chapter?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this chapter?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, chapterId: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

AdminChapterManager.propTypes = {
  onClose: PropTypes.func,
};

export default AdminChapterManager;
